import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { MfgUnit } from "@/lib/manufacturing/types";

export type MfgProductionLoadStatus = "open" | "dispatched" | "closed";

export type MfgProductionLoad = {
  id: string;
  organization_id: string;
  plant_id: string;
  code: string;
  alias: string;
  status: MfgProductionLoadStatus;
  footage_notes: string;
  dispatched_at: string | null;
  dispatched_by_user_id: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  unit_count: number;
};

export type MfgProductionLoadsListResponse = {
  ok: true;
  organization_id: string;
  loads: MfgProductionLoad[];
};

export type MfgProductionLoadMutationResponse = {
  ok: true;
  load: MfgProductionLoad;
  moved_unit_ids?: string[];
};

export type MfgProductionLoadAssignResponse = {
  ok: true;
  unit: MfgUnit;
};

export function listManufacturingProductionLoads(filters: {
  plant_id: string;
  status?: "active" | MfgProductionLoadStatus;
}) {
  const q = new URLSearchParams();
  q.set("plant_id", filters.plant_id);
  if (filters.status) q.set("status", filters.status);
  return manufacturingFetch<MfgProductionLoadsListResponse>(
    `/api/mfg/production-loads?${q.toString()}`,
    { method: "GET" },
  );
}

export function createManufacturingProductionLoad(payload: {
  plant_id: string;
  alias?: string;
  footage_notes?: string;
}) {
  return manufacturingFetch<MfgProductionLoadMutationResponse>("/api/mfg/production-loads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ op: "create", ...payload }),
  });
}

export function updateManufacturingProductionLoad(payload: {
  load_id: string;
  alias?: string;
  footage_notes?: string;
}) {
  return manufacturingFetch<MfgProductionLoadMutationResponse>("/api/mfg/production-loads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ op: "update", ...payload }),
  });
}

export function assignManufacturingUnitToProductionLoad(payload: {
  unit_id: string;
  load_id: string | null;
}) {
  return manufacturingFetch<MfgProductionLoadAssignResponse>("/api/mfg/production-loads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ op: "assign", ...payload }),
  });
}

export function dispatchManufacturingProductionLoad(payload: { load_id: string }) {
  return manufacturingFetch<MfgProductionLoadMutationResponse>("/api/mfg/production-loads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ op: "dispatch", ...payload }),
  });
}

export function closeManufacturingProductionLoad(payload: { load_id: string }) {
  return manufacturingFetch<MfgProductionLoadMutationResponse>("/api/mfg/production-loads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ op: "close", ...payload }),
  });
}

export function productionLoadLabel(load: Pick<MfgProductionLoad, "code" | "alias">): string {
  const alias = load.alias.trim();
  return alias ? `Load ${load.code} · ${alias}` : `Load ${load.code}`;
}
